import { ORDER_STATUSES, RETAILER_LABELS, STATUS_LABELS, STATUS_TONE, type DashboardStats } from '@nebueye/shared'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight, TrendingDown, TrendingUp, Truck } from 'lucide-react'
import { z } from 'zod'
import { ActivityChart, RetailerPerformanceChart } from '@/components/app/dashboard-charts'
import { Meter, OutcomeBar } from '@/components/app/outcome-bar'
import { ProductThumb, RetailerMark } from '@/components/app/retailer'
import { EmptyState, ErrorState, OrbitArt } from '@/components/app/states'
import { TONE_DOT } from '@/components/app/status-badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ago, count, moneyShort, percent, plural } from '@/lib/format'
import { dashboardQuery, incomingQuery } from '@/lib/queries'
import { sessionQuery } from '@/lib/session'
import { StatBar, Surface } from '@/components/app/surface'
import { cn } from '@/lib/utils'

const RANGES = [
  { days: 1, label: '24h', phrase: 'in the last 24 hours' },
  { days: 7, label: '7 days', phrase: 'in the last 7 days' },
  { days: 30, label: '30 days', phrase: 'in the last 30 days' },
  { days: 365, label: 'Year', phrase: 'this past year' },
] as const

export const Route = createFileRoute('/_app/')({
  validateSearch: z.object({ days: z.union([z.literal(1), z.literal(7), z.literal(30), z.literal(365)]).optional() }),
  loaderDeps: ({ search }) => ({ days: search.days ?? 7 }),
  loader: ({ context, deps }) => {
    context.queryClient.prefetchQuery(dashboardQuery(deps.days))
    context.queryClient.prefetchQuery(incomingQuery())
  },
  head: () => ({ meta: [{ title: 'Overview · Nebueye' }] }),
  component: Overview,
})

function greeting() {
  const h = new Date().getHours()
  return h < 5 ? 'Up late' : h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'
}

function Overview() {
  const days = Route.useSearch().days ?? 7
  const navigate = Route.useNavigate()
  const range = RANGES.find((r) => r.days === days) ?? RANGES[1]
  const user = useQuery(sessionQuery).data?.user
  const stats = useQuery(dashboardQuery(days))

  return (
    <div className="space-y-6">
      <section className="flex flex-wrap items-end justify-between gap-x-8 gap-y-4">
        <div className="min-w-0 space-y-2">
          <p className="text-muted-foreground text-sm">
            {greeting()}, {user?.name.split(' ')[0]}
          </p>
          {stats.data ? <Headline stats={stats.data} phrase={range.phrase} /> : <Skeleton className="h-9 w-80 max-w-full" />}
        </div>
        <Tabs value={String(days)} onValueChange={(v) => navigate({ search: { days: Number(v) as 1 | 7 | 30 | 365 } })}>
          <TabsList>
            {RANGES.map((r) => (
              <TabsTrigger key={r.days} value={String(r.days)} className="px-3">
                {r.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </section>

      {stats.isError ? (
        <ErrorState error={stats.error} onRetry={() => stats.refetch()} />
      ) : !stats.data ? (
        <OverviewSkeleton />
      ) : stats.data.totalOrders === 0 && stats.data.recentDrops.length === 0 ? (
        <EmptyState
          art={<OrbitArt />}
          title="Your orders will land here"
          action={
            <Button asChild variant="outline">
              <Link to="/settings" search={{ tab: 'integrations' }}>
                See how data gets in <ArrowRight />
              </Link>
            </Button>
          }
        >
          Once your confirmations start flowing in, this page answers the big questions: what went through, what got
          cancelled and what's arriving.
        </EmptyState>
      ) : (
        <>
          <ArrivingStrip />
          <Figures stats={stats.data} range={range.label} />
          <Surface className="p-5">
            <ActivityChart data={stats.data.ordersPerDay} hourly={days === 1} />
          </Surface>
          <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-6 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
            <Surface className="divide-y">
              <section className="space-y-4 p-5">
                <h2 className="text-[15px] font-semibold">By retailer</h2>
                <RetailerPerformanceChart rows={stats.data.byRetailer} />
              </section>
              <div className="p-5">
                <RecentDrops stats={stats.data} />
              </div>
            </Surface>
            <Surface className="divide-y">
              <div className="p-5">
                <Pipeline stats={stats.data} />
              </div>
              <div className="p-5">
                <TopProducts stats={stats.data} />
              </div>
              <div className="p-5">
                <StatusBreakdown stats={stats.data} />
              </div>
            </Surface>
          </div>
        </>
      )}
    </div>
  )
}

/** The answer first: one sentence with the numbers that matter. */
function Headline({ stats, phrase }: { stats: DashboardStats; phrase: string }) {
  const cancelled = stats.statusCounts.cancelled + stats.statusCounts.refunded
  const through = stats.totalOrders - cancelled
  if (stats.totalOrders === 0) return <h1 className="text-headline font-semibold">No orders {phrase}.</h1>
  return (
    <h1 className="text-headline tabular font-semibold text-balance">
      {plural(stats.totalOrders, 'order')} {phrase}.{' '}
      <span className="text-muted-foreground font-normal">
        <span className="text-status-success-text font-semibold">{count(through)} went through</span>
        {cancelled > 0 && <>, {count(cancelled)} cancelled</>}.
      </span>
    </h1>
  )
}

function ArrivingStrip() {
  const incoming = useQuery(incomingQuery()).data
  if (!incoming) return null
  const ofd = incoming.outForDelivery.length
  const today = incoming.deliveredToday.length
  const todayGroup = incoming.upcoming.find((g) => g.date === new Date().toLocaleDateString('en-CA'))?.orders.length ?? 0
  if (ofd + today + todayGroup === 0) return null
  return (
    <Link
      to="/incoming"
      className="group bg-accent text-accent-foreground flex items-center gap-3 rounded-lg px-4 py-3 text-sm transition-colors hover:brightness-[0.98]"
    >
      <Truck className="size-4 shrink-0" />
      <span className="tabular flex-1">
        {ofd > 0 && <strong className="font-semibold">{plural(ofd, 'package')} out for delivery</strong>}
        {ofd > 0 && (todayGroup > 0 || today > 0) && ' · '}
        {todayGroup > 0 && `${count(todayGroup)} expected today`}
        {todayGroup > 0 && today > 0 && ' · '}
        {today > 0 && `${count(today)} delivered today`}
      </span>
      <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
    </Link>
  )
}

/** Change vs the previous window; `goodWhenUp` flips colour for rates like cancels. */
function Delta({ now, before, goodWhenUp = true, asPoints = false }: { now: number; before: number; goodWhenUp?: boolean; asPoints?: boolean }) {
  if (before === 0 && !asPoints) return <span className="text-subtle text-xs">No prior data</span>
  const change = asPoints ? (now - before) * 100 : ((now - before) / before) * 100
  if (Math.abs(change) < 0.5) return <span className="text-subtle text-xs">No change</span>
  const up = change > 0
  const good = up === goodWhenUp
  const Icon = up ? TrendingUp : TrendingDown
  return (
    <span className={cn('tabular inline-flex items-center gap-1 text-xs font-medium', good ? 'text-status-success-text' : 'text-status-danger-text')}>
      <Icon className="size-3.5" aria-hidden />
      <span className="sr-only">{up ? 'Up' : 'Down'}</span>
      {Math.abs(change).toFixed(asPoints ? 1 : 0)}
      {asPoints ? ' pts' : '%'}
    </span>
  )
}

function Figures({ stats, range }: { stats: DashboardStats; range: string }) {
  const p = stats.previous
  const prevSuccess = p.totalOrders === 0 ? 0 : p.keptOrders / p.totalOrders
  const success = stats.totalOrders === 0 ? 0 : stats.keptOrders / stats.totalOrders
  const items = [
    {
      label: 'Orders',
      value: count(stats.totalOrders),
      sub: `${count(stats.ordersToday)} today`,
      delta: <Delta now={stats.totalOrders} before={p.totalOrders} />,
    },
    {
      label: 'Success rate',
      value: percent(success),
      sub: `${count(stats.keptOrders)} went through`,
      delta: p.totalOrders ? <Delta now={success} before={prevSuccess} asPoints /> : <Delta now={0} before={0} />,
    },
    {
      label: 'Spent',
      value: moneyShort(stats.totalSpend),
      sub: `${count(stats.keptUnits)} units · ${moneyShort(stats.avgOrderValue)} avg`,
      delta: <Delta now={stats.totalSpend} before={p.totalSpend} />,
    },
    {
      label: 'Shipped',
      value: percent(stats.shipRate),
      sub: `${count(stats.pipeline.awaitingShipment)} waiting to ship`,
      delta: null,
    },
  ]
  return (
    <StatBar
      stats={items.map((it) => ({
        label: it.label,
        value: <span className="text-headline">{it.value}</span>,
        sub: (
          <span className="mt-1 block space-y-0.5">
            <span className="text-muted-foreground block">{it.sub}</span>
            {it.delta && p.totalOrders > 0 && (
              <span className="flex flex-wrap items-center gap-x-1.5">
                {it.delta}
                <span>vs prior {range}</span>
              </span>
            )}
          </span>
        ),
      }))}
    />
  )
}

function Pipeline({ stats }: { stats: DashboardStats }) {
  const { awaitingShipment: a, inTransit: t, delivered: d } = stats.pipeline
  const total = a + t + d
  const steps = [
    { label: 'Waiting to ship', n: a, dot: 'bg-status-neutral' },
    { label: 'In transit', n: t, dot: 'bg-status-progress' },
    { label: 'Delivered', n: d, dot: 'bg-status-success' },
  ]
  return (
    <section className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h2 className="text-[15px] font-semibold">Fulfilment</h2>
        <Link to="/incoming" className="text-primary text-[13px] font-medium hover:underline">
          Incoming
        </Link>
      </div>
      {total === 0 ? (
        <p className="text-muted-foreground text-sm">Nothing went through in this range yet.</p>
      ) : (
        <>
          <Meter
            label={`${count(d)} of ${count(total)} delivered`}
            className="h-2.5"
            segments={steps.map((s) => ({ value: s.n, className: s.dot }))}
          />
          <dl className="grid grid-cols-3 gap-3">
            {steps.map((s) => (
              <div key={s.label} className="space-y-0.5">
                <dt className="text-muted-foreground flex items-center gap-1.5 text-[13px]">
                  <span className={`size-2 rounded-full ${s.dot}`} aria-hidden />
                  {s.label}
                </dt>
                <dd className="text-title tabular font-semibold">{count(s.n)}</dd>
                <dd className="text-subtle tabular text-xs">{percent(s.n / total)}</dd>
              </div>
            ))}
          </dl>
        </>
      )}
    </section>
  )
}

function TopProducts({ stats }: { stats: DashboardStats }) {
  const max = Math.max(1, ...stats.topProducts.map((p) => p.keptUnits))
  return (
    <section className="space-y-3">
      <h2 className="text-[15px] font-semibold">Top products</h2>
      {stats.topProducts.length === 0 ? (
        <p className="text-muted-foreground py-6 text-sm">Your best performers show up here.</p>
      ) : (
        <ol className="space-y-3.5">
          {stats.topProducts.map((p, i) => (
            <li key={`${p.retailer}-${p.name}`} className="flex items-center gap-3">
              <span className="text-subtle tabular w-3 text-xs">{i + 1}</span>
              <ProductThumb src={p.imageUrl} retailer={p.retailer} className="size-9" />
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex items-baseline justify-between gap-3">
                  <p className="truncate text-sm font-medium">{p.name}</p>
                  <p className="tabular shrink-0 text-sm font-semibold">{moneyShort(p.spend)}</p>
                </div>
                <div className="bg-surface-sunken h-1.5 overflow-hidden rounded-full">
                  <div className="bg-chart-1 h-full rounded-full" style={{ width: `${(p.keptUnits / max) * 100}%` }} />
                </div>
                <p className="text-subtle tabular text-xs">
                  {RETAILER_LABELS[p.retailer]} · {count(p.keptUnits)} units kept
                </p>
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  )
}

function RecentDrops({ stats }: { stats: DashboardStats }) {
  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between">
        <h2 className="text-[15px] font-semibold">Latest drops</h2>
        <Link to="/drops" className="text-primary text-[13px] font-medium hover:underline">
          All drops
        </Link>
      </div>
      {stats.recentDrops.length === 0 ? (
        <p className="text-muted-foreground py-6 text-sm">Drops appear when several orders for one product land close together.</p>
      ) : (
        <ul className="divide-y">
          {stats.recentDrops.map((d) => (
            <li key={d.id}>
              <Link
                to="/drops/$id"
                params={{ id: d.id }}
                className="hover:bg-surface-sunken -mx-3 flex items-center gap-3 rounded-md px-3 py-3 transition-colors"
              >
                <RetailerMark retailer={d.retailer} />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="truncate text-sm font-medium">{d.productName}</p>
                    <p className="tabular shrink-0 text-sm">
                      <span className="font-semibold">{d.confirmedOrders}</span>
                      <span className="text-muted-foreground">/{d.totalOrders}</span>
                    </p>
                  </div>
                  <OutcomeBar kept={d.confirmedOrders} cancelled={d.cancelledOrders} />
                  <p className="text-subtle text-xs">
                    {RETAILER_LABELS[d.retailer]} · {ago(d.dropStartedAt)}
                  </p>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function StatusBreakdown({ stats }: { stats: DashboardStats }) {
  const rows = ORDER_STATUSES.map((s) => ({ status: s, n: stats.statusCounts[s] })).filter((r) => r.n > 0)
  const total = rows.reduce((s, r) => s + r.n, 0)
  return (
    <section className="space-y-4">
      <h2 className="text-[15px] font-semibold">Every status</h2>
      {total === 0 ? (
        <p className="text-muted-foreground text-sm">Nothing in this range.</p>
      ) : (
        <>
          <Meter
            label={`${count(total)} orders by status`}
            className="h-2.5"
            segments={rows.map((r) => ({ value: r.n, className: TONE_DOT[STATUS_TONE[r.status]] }))}
          />
          <ul className="grid gap-x-10 gap-y-2.5 sm:grid-cols-2 lg:grid-cols-1">
            {rows.map((r) => (
              <li key={r.status}>
                <Link
                  to="/orders"
                  search={{ status: r.status }}
                  className="hover:text-primary flex items-center gap-2.5 text-sm transition-colors"
                >
                  <span className={`size-2 rounded-full ${TONE_DOT[STATUS_TONE[r.status]]}`} aria-hidden />
                  <span className="flex-1">{STATUS_LABELS[r.status]}</span>
                  <span className="tabular font-medium">{count(r.n)}</span>
                  <span className="tabular text-subtle w-10 text-right text-xs">{percent(r.n / total)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  )
}

function OverviewSkeleton() {
  return (
    <div className="space-y-10" aria-busy="true" aria-label="Loading overview">
      <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-3.5 w-24" />
            <Skeleton className="h-6 w-16" />
          </div>
        ))}
      </div>
      <Skeleton className="h-44 w-full" />
      <div className="grid gap-10 lg:grid-cols-2">
        <Skeleton className="h-56" />
        <Skeleton className="h-56" />
      </div>
    </div>
  )
}
